import {DoppeViewerAppClientServiceBase} from './doppe-viewer-app-client-service-base';
import type {DoppeDtoBymoLinkViewerData} from '../../client-server-common/types/doppe-dto-bymo-link-viewer-data';
import {
    browserHistorySupportedWithoutUserGesture,
    DomEventListener,
    PointerInteraction,
    SessionStorageAccessor,
    UserGestureTracker
} from '@wix/devzai-utils-dom';
import {assertDefined, Direction, resourceCreate, Timer} from '@wix/devzai-utils-common';
import {DoppeBiEmptyTargetUrl, doppeViewerPageInfoResolvePageId} from '../../client-server-common';
import type {DoppeDtoAction} from '../../client-server-common/types/doppe-dto-action';

const DisableBackTrapSessionStorageKey = 'doppe.disable-back-trap';
const backTrapStateValue = 'bymo-viewer-back-trap';

export class DoppeViewerAppBymoService extends DoppeViewerAppClientServiceBase {

    private skippedInPageView = false;

    private bymoLinkViewerData?: DoppeDtoBymoLinkViewerData.WithConnectedPage = undefined;

    public readonly observableActionsSettingsOverrides = resourceCreate<Record<DoppeDtoAction.Id, {}>>({
        initialValue: {}
    });

    initialize (
        options: {
            bymoLinkViewerData: DoppeDtoBymoLinkViewerData.WithConnectedPage;
        }
    ) {
        this.bymoLinkViewerData = options.bymoLinkViewerData;
    }

    public setActionSettingsOverrides<T extends {}> (actionId: DoppeDtoAction.Id, settings: T): void {
        this.observableActionsSettingsOverrides.modify((settingsOverrides) => {
            settingsOverrides[actionId] = settings;
        });
    }

    public getBymoPageProps () {
        return this.getBymoLinkViewerData().pageData.bymoPageProps;
    }

    public getBymoLinkViewerData () {
        return assertDefined(this.bymoLinkViewerData, 'DoppeViewerAppBymoService is not initialized.');
    }

    initializePageListeners (
        options: {
            livePageData?: DoppeDtoBymoLinkViewerData.LivePageData;
            logUserActivityEvent?: boolean;
            targetUrl: string | null;
        }
    ) {
        const {
            livePageData,
            logUserActivityEvent = false,
            targetUrl,
        } = options;

        if(livePageData) {
            const activityTimer = new Timer(1000, () => {
                const currentPageInfo = this.clientServices.viewerRouterClientService.getCurrentPageInfo();
                const bymoPageProps = this.getBymoPageProps();
                this.clientServices.viewerAnalyticsClient.logEvent('uouActivity', {
                    pageId: livePageData.bymoPageId,
                    hypeUserId: livePageData.doppeUserId,
                    linkId: livePageData.linkId,
                    destinationUrl: targetUrl ?? DoppeBiEmptyTargetUrl,
                    innerPageId: doppeViewerPageInfoResolvePageId(currentPageInfo, bymoPageProps) ?? undefined,
                });
            })

            if (document.visibilityState === 'visible') {
                const currentPageInfoForVisibility = this.clientServices.viewerRouterClientService.getCurrentPageInfo();
                const bymoPagePropsForVisibility = this.getBymoPageProps();
                this.clientServices.viewerAnalyticsClient.logEvent('uouPageVisibilityChange', {
                    pageId: livePageData.bymoPageId,
                    hypeUserId: livePageData.doppeUserId,
                    linkId: livePageData.linkId,
                    state: document.visibilityState,
                    destinationUrl: targetUrl ?? DoppeBiEmptyTargetUrl,
                    pageType: livePageData.pageType,
                    innerPageId: doppeViewerPageInfoResolvePageId(currentPageInfoForVisibility, bymoPagePropsForVisibility) ?? undefined,
                });

                if (logUserActivityEvent) {
                    activityTimer.activate();
                }

                const pageUnloadDate = SessionStorageAccessor.getItem<number>(
                    `pageUnloadDate: ${livePageData.doppeUserId} ${livePageData.bymoPageId} ${livePageData.linkId}`
                );

                const skippedDate = SessionStorageAccessor.getItem<number>(
                    `skippedDate: ${livePageData.doppeUserId} ${livePageData.bymoPageId} ${livePageData.linkId}`
                );

                const currentDate = Date.now();
                if(!skippedDate && pageUnloadDate !== undefined && (currentDate - pageUnloadDate) < 5000) {
                    this.clientServices.viewerAnalyticsClient.logEvent('uouPageRefresh', {
                        pageId: livePageData.bymoPageId,
                        hypeUserId: livePageData.doppeUserId,
                        linkId: livePageData.linkId,
                        destinationUrl: targetUrl ?? DoppeBiEmptyTargetUrl,
                        pageType: livePageData.pageType,
                        innerPageId: doppeViewerPageInfoResolvePageId(currentPageInfoForVisibility, bymoPagePropsForVisibility) ?? undefined,
                    });
                }
            }

            // Visibility Changed - Focus Changed
            const domEventListener = new DomEventListener(
                () => document,
                'visibilitychange',
                () => {

                    const visibilityState = document.visibilityState;

                    if (logUserActivityEvent) {
                        if (visibilityState === 'visible') {
                            activityTimer.activate();
                        } else {
                            activityTimer.deactivate();
                        }
                    }

                    const currentPageInfoForVisibilityChange = this.clientServices.viewerRouterClientService.getCurrentPageInfo();
                    const bymoPagePropsForChange = this.getBymoPageProps();
                    this.clientServices.viewerAnalyticsClient.logEvent('uouPageVisibilityChange', {
                        pageId: livePageData.bymoPageId,
                        hypeUserId: livePageData.doppeUserId,
                        linkId: livePageData.linkId,
                        state: document.visibilityState,
                        destinationUrl: targetUrl ?? DoppeBiEmptyTargetUrl,
                        pageType: livePageData.pageType,
                        innerPageId: doppeViewerPageInfoResolvePageId(currentPageInfoForVisibilityChange, bymoPagePropsForChange) ?? undefined,
                    });

                    this.clientServices.viewerAnalyticsClient.flushLogs();
                }
            );

            domEventListener.activate();

            // page unload
            const domEventListenerUnload = new DomEventListener(
                () => window,
                'beforeunload',
                () => {
                    const currentPageInfoForUnload = this.clientServices.viewerRouterClientService.getCurrentPageInfo();
                    const bymoPagePropsForUnload = this.getBymoPageProps();
                    const innerPageIdForUnload = doppeViewerPageInfoResolvePageId(currentPageInfoForUnload, bymoPagePropsForUnload) ?? undefined;
                    this.clientServices.viewerAnalyticsClient.logEvent('uouPageUnload', {
                        pageId: livePageData.bymoPageId,
                        hypeUserId: livePageData.doppeUserId,
                        linkId: livePageData.linkId,
                        destinationUrl: targetUrl ?? DoppeBiEmptyTargetUrl,
                        pageType: livePageData.pageType,
                        innerPageId: innerPageIdForUnload,
                    });

                    SessionStorageAccessor.updateItem<number>(
                        `pageUnloadDate: ${livePageData.doppeUserId} ${livePageData.bymoPageId} ${livePageData.linkId}`,
                        () => Date.now()
                    );

                    if(!this.skippedInPageView) {

                        SessionStorageAccessor.updateItem<number | null>(
                            `skippedDate: ${livePageData.doppeUserId} ${livePageData.bymoPageId} ${livePageData.linkId}`, () => null
                        );

                        this.clientServices.viewerAnalyticsClient.logEvent('uouBounced', {
                            pageId: livePageData.bymoPageId,
                            hypeUserId: livePageData.doppeUserId,
                            linkId: livePageData.linkId,
                            destinationUrl: targetUrl ?? DoppeBiEmptyTargetUrl,
                            pageType: livePageData.pageType,
                            innerPageId: innerPageIdForUnload,
                        });
                    } else {
                        this.clientServices.viewerAnalyticsClient.logEvent('uouProceeded', {
                            pageId: livePageData.bymoPageId,
                            hypeUserId: livePageData.doppeUserId,
                            linkId: livePageData.linkId,
                            destinationUrl: targetUrl ?? DoppeBiEmptyTargetUrl,
                            pageType: livePageData.pageType,
                        });
                    }

                    this.clientServices.viewerAnalyticsClient.flushLogs();
                }
            );

            domEventListenerUnload.activate();


        }
    }

    initializeSkipOnBackBehaviour (
        options: {
            targetUrl: string;
            livePageData?: DoppeDtoBymoLinkViewerData.LivePageData
        }
    ) {
        const {
            targetUrl,
            livePageData
        } = options;

        const disableBackTrapForCurrentSession = SessionStorageAccessor.getItem<boolean>(DisableBackTrapSessionStorageKey) ?? false;

        if (!disableBackTrapForCurrentSession) {
            UserGestureTracker.ensureInitialized();

            const supportedWithoutUserGesture = browserHistorySupportedWithoutUserGesture();

            if (history.state !== backTrapStateValue) {

                const setBackTrap = () => {
                    history.replaceState(backTrapStateValue, '');
                    history.pushState(null, '');
                }

                if (supportedWithoutUserGesture) {
                    setBackTrap();
                } else {

                    // A hack that starts the page with manual scrolling only until the first touch down.
                    document.body.style.touchAction = 'none';
                    DomEventListener.one(document.documentElement, 'touchstart', (evt: TouchEvent) => {
                        document.body.style.touchAction = 'auto';
                        const initialScrollTop = document.documentElement.scrollTop;

                        PointerInteraction.start(evt, {
                            allowedDirections: [Direction.Up, Direction.Down],
                            startCallback: interaction => {
                                return {
                                    moveCallback: () => {
                                        document.documentElement.scrollTop = initialScrollTop - interaction.getTotalFixedDeltaY();
                                    }
                                }
                            }
                        })
                    })

                    UserGestureTracker.executeWhenUserGestureBlockedFeaturesAreEnabled(setBackTrap)
                }
            }
        }


        new DomEventListener(window, 'popstate', () => {
            if (history.state === backTrapStateValue) {

                if (disableBackTrapForCurrentSession) {
                    history.back();
                } else {
                    if (livePageData) {
                        this.clientServices.viewerAnalyticsClient.logEvent('uouDestinationRedirectedOnBack', {
                            pageId: livePageData.bymoPageId,
                            hypeUserId: livePageData.doppeUserId,
                            linkId: livePageData.linkId,
                            afterUserGesture: UserGestureTracker.userGestureOccurred,
                            destinationUrl: targetUrl,
                        });

                        const currentPageInfo = this.clientServices.viewerRouterClientService.getCurrentPageInfo();
                        const bymoPagePropsForEngagement = this.getBymoPageProps();
                        this.clientServices.viewerAnalyticsClient.logEvent('uouEngagementClicked', {
                            pageId: livePageData.bymoPageId,
                            hypeUserId: livePageData.doppeUserId,
                            linkId: livePageData.linkId,
                            destinationUrl: targetUrl,
                            innerPageId: doppeViewerPageInfoResolvePageId(currentPageInfo, bymoPagePropsForEngagement) ?? undefined,
                        });
                    }



                    this.skipToTargetUrl({
                        targetUrl: targetUrl,
                        livePageData: livePageData,
                        replaceCurrentPage: true
                    })
                }
            }
        }).activate()
    }

    skipToTargetUrl (
        options: {
            targetUrl: string;
            replaceCurrentPage?: boolean;
            livePageData?: DoppeDtoBymoLinkViewerData.LivePageData;
        }
    ) {
        const {
            replaceCurrentPage = false,
            targetUrl,
            livePageData
        } = options;


        if (livePageData) {
            this.clientServices.viewerAnalyticsClient.logEvent('uouDestinationRedirected', {
                pageId: livePageData.bymoPageId,
                hypeUserId: livePageData.doppeUserId,
                linkId: livePageData.linkId,
                passThrough: false,
                destinationUrl: targetUrl,
            });

            SessionStorageAccessor.updateItem<number>(
                `skippedDate: ${livePageData.doppeUserId} ${livePageData.bymoPageId} ${livePageData.linkId}`,
                () => Date.now()
            );
        }

        SessionStorageAccessor.setItem(DisableBackTrapSessionStorageKey, true);

        this.redirectToUrl({
            targetUrl: targetUrl,
            replaceCurrentPage: replaceCurrentPage
        })
    }

    redirectToUrl(
        options: {
            targetUrl: string,
            replaceCurrentPage: boolean
        }
    ) {

        const {
            replaceCurrentPage,
            targetUrl,
        } = options;

        this.skippedInPageView = true;

        if (replaceCurrentPage) {
            location.replace(targetUrl);
        } else {
            location.href = targetUrl;
        }
    }
}